import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UserMetrics {
  total_users: number;
  daily_active_users: number;
  weekly_active_users: number;
  monthly_active_users: number;
  onboarding_completion_pct: number;
  retention_7d_pct: number;
  retention_30d_pct: number;
  new_users_today: number;
  new_users_week: number;
  new_users_month: number;
}

interface ProjectMetrics {
  total_projects: number;
  projects_today: number;
  projects_week: number;
  projects_month: number;
  avg_files_per_project: number;
  avg_ai_actions_per_project: number;
  projects_with_deploys: number;
}

interface AIMetrics {
  total_requests: number;
  requests_today: number;
  requests_week: number;
  avg_requests_per_user: number;
  scaffold_count: number;
  code_agent_count: number;
  avg_tokens_per_request: number;
  success_count: number;
  failure_count: number;
  success_rate: number;
}

interface DeployMetrics {
  total_deploys: number;
  deploys_today: number;
  deploys_week: number;
  successful_deploys: number;
  failed_deploys: number;
  success_rate: number;
  avg_deploys_per_user: number;
  unique_deployers: number;
}

interface CollaborationMetrics {
  projects_with_collaborators: number;
  avg_collaborators_per_project: number;
  total_chat_messages: number;
  messages_today: number;
  messages_week: number;
  active_collaborations: number;
  total_invites: number;
  accepted_invites: number;
}

interface TimelineDataPoint {
  date: string;
  new_users: number;
  new_projects: number;
  ai_requests: number;
  deploys: number;
  chat_messages: number;
}

export function useAdminMetrics(daysBack: number = 30) {
  const userMetrics = useQuery({
    queryKey: ["admin-user-metrics", daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_user_metrics", {
        days_back: daysBack,
      });
      if (error) throw error;
      return (data?.[0] || null) as UserMetrics | null;
    },
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  const projectMetrics = useQuery({
    queryKey: ["admin-project-metrics", daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_project_metrics", {
        days_back: daysBack,
      });
      if (error) throw error;
      return (data?.[0] || null) as ProjectMetrics | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const aiMetrics = useQuery({
    queryKey: ["admin-ai-metrics", daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_ai_metrics", {
        days_back: daysBack,
      });
      if (error) throw error;
      return (data?.[0] || null) as AIMetrics | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const deployMetrics = useQuery({
    queryKey: ["admin-deploy-metrics", daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_deploy_metrics", {
        days_back: daysBack,
      });
      if (error) throw error;
      return (data?.[0] || null) as DeployMetrics | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const collaborationMetrics = useQuery({
    queryKey: ["admin-collaboration-metrics", daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_collaboration_metrics", {
        days_back: daysBack,
      });
      if (error) throw error;
      return (data?.[0] || null) as CollaborationMetrics | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const timeline = useQuery({
    queryKey: ["admin-metrics-timeline", daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_metrics_timeline", {
        days_back: daysBack,
      });
      if (error) throw error;
      return (data || []) as TimelineDataPoint[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const isLoading =
    userMetrics.isLoading ||
    projectMetrics.isLoading ||
    aiMetrics.isLoading ||
    deployMetrics.isLoading ||
    collaborationMetrics.isLoading ||
    timeline.isLoading;

  return {
    userMetrics: userMetrics.data,
    projectMetrics: projectMetrics.data,
    aiMetrics: aiMetrics.data,
    deployMetrics: deployMetrics.data,
    collaborationMetrics: collaborationMetrics.data,
    timeline: timeline.data,
    isLoading,
    refetchAll: () => {
      userMetrics.refetch();
      projectMetrics.refetch();
      aiMetrics.refetch();
      deployMetrics.refetch();
      collaborationMetrics.refetch();
      timeline.refetch();
    },
  };
}

export function useIsAdmin() {
  return useQuery({
    queryKey: ["is-admin"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      
      if (error) return false;
      return !!data;
    },
    staleTime: 10 * 60 * 1000, // 10 min cache
  });
}
